import { useState, useEffect, useCallback } from 'react';
import { perjudicadosService } from '../services/perjudicadosService';
import ConfirmDialog from './ConfirmDialog';
import { useConfirm } from '../hooks/useConfirm';

const VACIO = { perjudicado: '', domicilio: '', telefono1: '', telefono2: '' };

export default function PerjudicadosPanel({ idSiniestro, soloLectura = false }) {
  const [lista, setLista]           = useState([]);
  const [isLoading, setIsLoading]   = useState(false);
  const [form, setForm]             = useState(VACIO);
  const [editandoId, setEditandoId] = useState(null);
  const [guardando, setGuardando]   = useState(false);
  const [eliminando, setEliminando] = useState(null);
  const [error, setError]           = useState(null);
  const { confirm, pedirConfirm }   = useConfirm();

  const cargar = useCallback(async () => {
    setIsLoading(true);
    try {
      const r = await perjudicadosService.listar(idSiniestro);
      setLista(r.data);
    } catch {
      setError('Error al cargar perjudicados');
    } finally {
      setIsLoading(false);
    }
  }, [idSiniestro]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleChange = e =>
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleNuevo = () => {
    setEditandoId('nuevo');
    setForm(VACIO);
    setError(null);
  };

  const handleEditar = (p) => {
    setEditandoId(p.id);
    setForm({ perjudicado: p.perjudicado, domicilio: p.domicilio, telefono1: p.telefono1 || '', telefono2: p.telefono2 || '' });
    setError(null);
  };

  const handleCancelar = () => { setEditandoId(null); setForm(VACIO); };

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      if (editandoId === 'nuevo') {
        await perjudicadosService.crear(idSiniestro, form);
      } else {
        await perjudicadosService.actualizar(idSiniestro, editandoId, form);
      }
      setEditandoId(null);
      setForm(VACIO);
      await cargar();
    } catch {
      setError('Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (id) => {
    const ok = await pedirConfirm({
      titulo: 'Eliminar perjudicado',
      mensaje: '¿Eliminar este perjudicado? Esta acción no se puede deshacer.',
      peligro: true,
      textOk: 'Eliminar',
    });
    if (!ok) return;
    setEliminando(id);
    try {
      await perjudicadosService.eliminar(idSiniestro, id);
      await cargar();
    } catch {
      setError('Error al eliminar');
    } finally {
      setEliminando(null);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
          Perjudicados
          {lista.length > 0 && (
            <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-normal normal-case">
              {lista.length}
            </span>
          )}
        </h3>
        {editandoId === null && !soloLectura && (
          <button onClick={handleNuevo}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium transition">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
            </svg>
            Añadir
          </button>
        )}
      </div>

      {error && <p role="alert" className="text-xs text-red-600">{error}</p>}

      {/* Formulario inline */}
      {editandoId !== null && !soloLectura && (
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <label className="text-xs text-gray-500 mb-0.5 block">Nombre *</label>
              <input name="perjudicado" value={form.perjudicado} onChange={handleChange}
                placeholder="Nombre del perjudicado"
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-500 mb-0.5 block">Domicilio / Piso</label>
              <input name="domicilio" value={form.domicilio} onChange={handleChange}
                placeholder="Ej: Piso 2º A"
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">Teléfono 1</label>
              <input name="telefono1" value={form.telefono1} onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">Teléfono 2</label>
              <input name="telefono2" value={form.telefono2} onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={handleGuardar} disabled={guardando}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold py-1.5 rounded-lg transition disabled:opacity-50">
              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
            <button onClick={handleCancelar}
              className="flex-1 border border-gray-300 text-gray-600 text-xs font-medium py-1.5 rounded-lg hover:bg-gray-50 transition">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lista */}
      {isLoading ? (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"/>
        </div>
      ) : lista.length === 0 && editandoId === null ? (
        <p className="text-xs text-gray-400 text-center py-3">Sin perjudicados</p>
      ) : (
        <div className="space-y-1">
          {lista.map(p => (
            <div key={p.id}
              className="flex items-start justify-between py-2 px-3 rounded-lg hover:bg-gray-50 group">
              <div>
                <p className="text-sm font-medium text-gray-900">{p.perjudicado}</p>
                {p.domicilio && <p className="text-xs text-gray-500">{p.domicilio}</p>}
                {(p.telefono1 || p.telefono2) && (
                  <p className="text-xs text-gray-400">
                    {[p.telefono1, p.telefono2].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
              {!soloLectura && (
              <div className="flex gap-1 shrink-0 ml-2">
                <button onClick={() => handleEditar(p)} title="Editar"
                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
                  </svg>
                </button>
                <button onClick={() => handleEliminar(p.id)} title="Eliminar"
                  aria-label="Eliminar perjudicado"
                  disabled={eliminando === p.id}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-40">
                  {eliminando === p.id
                    ? <div className="w-3.5 h-3.5 animate-spin rounded-full border-2 border-red-400 border-t-transparent"/>
                    : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                  }
                </button>
              </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog {...confirm} abierto={confirm !== null} />
    </div>
  );
}
